import React, { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import OptionsGrid from "./OptionsGrid";
import OptionsList from "./OptionsList";
import HoverTooltip from "./HoverTooltip";
import logo from "../../assets/BlueGecko Chat Icon.png";
import { agentApiInstance } from "../../api-client/AxiosClient";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";

const GREETINGS = [
  "hi",
  "hello",
  "hey",
  "greetings",
  "good morning",
  "good afternoon",
  "good evening",
];

const WELCOME_MESSAGE = {
  id: "welcome",
  type: "bot",
  content: `# Hello! 👋 

I'm your mapping assistant. I can help you with:

* SAP S/4HANA migration queries
* Technical documentation
* Best practices and guidelines
* General questions about SAP

How can I assist you today?`,
};

const ROLES = ["data steward", "transformation lead", "data owner"];

const useChatManager = () => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  return {
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    isLoading,
    setIsLoading,
    error,
    setError,
  };
};

const useSessionManager = () => {
  const [sessionId, setSessionId] = useState(() => Date.now().toString());
  const [sessionActive, setSessionActive] = useState(false);
  const [isFirstSession, setIsFirstSession] = useState(true);

  const startNewSession = () => {
    const newSessionId = Date.now().toString();
    setSessionId(newSessionId);
    setSessionActive(true);
    setIsFirstSession(false);
    return newSessionId;
  };

  return {
    sessionId,
    setSessionId,
    sessionActive,
    setSessionActive,
    isFirstSession,
    setIsFirstSession,
    startNewSession,
  };
};

const useOptionsManager = (sendMessage, setIsFirstSession) => {
  const [showOptions, setShowOptions] = useState(true);
  const [showOptionsList, setShowOptionsList] = useState(false);
  const [hoverInfo, setHoverInfo] = useState({
    show: false,
    content: "",
    x: 0,
    y: 0,
  });
  const optionsListRef = useRef(null);
  const plusButtonRef = useRef(null);

  useEffect(() => {
    // Handle clicks outside the options list and tooltip
    const handleClickOutside = (event) => {
      if (
        showOptionsList &&
        optionsListRef.current &&
        !optionsListRef.current.contains(event.target) &&
        !plusButtonRef.current.contains(event.target)
      ) {
        setShowOptionsList(false);
      }

      if (hoverInfo.show && !event.target.closest(".option-item")) {
        setHoverInfo((prev) => ({ ...prev, show: false }));
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showOptionsList, hoverInfo.show]);

  const toggleOptionsList = () => {
    setShowOptionsList((prev) => !prev);
  };

  const handleOptionHover = (content, event) => {
    if (!content) return;

    const rect = event.currentTarget.getBoundingClientRect();
    setHoverInfo({
      show: true,
      content,
      x: rect.right + 10,
      y: rect.top,
    });
  };

  const handleOptionLeave = () => {
    setHoverInfo((prev) => ({ ...prev, show: false }));
  };

  const handleSelectOption = (option) => {
    setShowOptions(false);
    setShowOptionsList(false);
    setIsFirstSession(false);

    if (typeof option === "string" && option.trim()) {
      sendMessage(option);
    }
  };

  return {
    showOptions,
    setShowOptions,
    showOptionsList,
    setShowOptionsList,
    hoverInfo,
    optionsListRef,
    plusButtonRef,
    toggleOptionsList,
    handleOptionHover,
    handleOptionLeave,
    handleSelectOption,
  };
};

// Main Chat Interface Component
const ChatInterface = () => {
  const messagesEndRef = useRef(null);
  const [apiError, setApiError] = useState(null);
  const [collections, setCollections] = useState(["string"]); // Added default collection
  const [deepThinking, setDeepThinking] = useState(false);
  // Custom hooks for managing different parts of the app
  const {
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    isLoading,
    setIsLoading,
    error,
    setError,
  } = useChatManager();

  const {
    sessionId,
    setSessionId,
    sessionActive,
    setSessionActive,
    isFirstSession,
    setIsFirstSession,
    startNewSession,
  } = useSessionManager();

  // Send message function that can be passed to other components
  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      type: "user",
      content: messageText,
    };

    // const newMessages = [...messages, userMessage];
    // setMessages(newMessages);
    const newMessages = [...messages, userMessage];

    if (deepThinking) {
      // Only add temp message if not already present
      const hasTemp = newMessages.some((msg) => msg.temp);
      if (!hasTemp) {
        const thinkingMessage = {
          id: Date.now().toString(),
          type: "bot",
          content: "_Thinking..._",
          temp: true,
        };
        newMessages.push(thinkingMessage);
      }
    }

    setMessages(newMessages);

    setInputMessage("");
    setIsLoading(true);
    setError(null);
    // Do not auto-enable deep thinking; only user can toggle

    try {
      // Check if the message is a greeting
      if (GREETINGS.includes(messageText.toLowerCase().trim())) {
        const greetingResponse = {
          id: (Date.now() + 1).toString(),
          type: "bot",
          content: `### Hello! 👋 

I'm ready to help you with your SAP-related questions. You can:

* Ask about SAP migrations
* Request technical documentation
* Get best practices
* Query general SAP information

What would you like to know?`,
        };

        setMessages([...newMessages, greetingResponse]);
        setApiError(null);
        if (!sessionActive) {
          setSessionActive(true);
        }
        return;
      }

      try {
        let queryPayload = {
          max_results: 3,
          model: "gpt-4o",
          collections: collections, // Use the state value
          format: "simple",
          show_reasoning_chain: deepThinking ? "yes" : "no",
        };

        if (
          messages.length === 1 &&
          ROLES.includes(messageText.toLowerCase())
        ) {
          queryPayload = { ...queryPayload, role: messageText, query: "" };
        } else {
          queryPayload = { ...queryPayload, query: messageText };
        }

        // Make the API request to the /query endpoint
        const response = await agentApiInstance.post(
          "/AgenticAIMapping",
          queryPayload
        );

        const formattedExplanation = response.data.explanation.replace(
          /\\n/g,
          "\n"
        );

        const botMessage = {
          id: Date.now().toString(),
          type: "bot",
          content: formattedExplanation, // ← keep it as markdown
          sources: response.data.sources || [],
        };

        // setMessages([...newMessages, botMessage]);
        // Remove temporary "Thinking..." if exists
        const filteredMessages = newMessages.filter((msg) => !msg.temp);
        setMessages([...filteredMessages, botMessage]);

        setApiError(null);
      } catch (err) {
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          type: "bot",
          content: `Sorry, I couldn't process your request: ${
            err.message || "Unknown error"
          }`,
          sources: [],
        };

        setApiError(
          err.message || "Failed to get response from knowledge base"
        );
        // setMessages([...newMessages, errorMessage]);
        const filteredMessages = newMessages.filter((msg) => !msg.temp);
        setMessages([...filteredMessages, errorMessage]);
      }
    } catch (error) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
      // Do not auto-disable deep thinking; user controls toggle
    }
  };

  const {
    showOptions,
    setShowOptions,
    showOptionsList,
    setShowOptionsList,
    hoverInfo,
    optionsListRef,
    plusButtonRef,
    toggleOptionsList,
    handleOptionHover,
    handleOptionLeave,
    handleSelectOption,
  } = useOptionsManager(sendMessage, setIsFirstSession);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage) return;

    // Hide options if they're showing
    if (showOptions || showOptionsList) {
      setShowOptions(false);
      setShowOptionsList(false);
      setIsFirstSession(false);
    }

    sendMessage(trimmedMessage);
  };

  return (
    <div className="flex h-screen bg-cover bg-center bg-opacity-50 relative overflow-hidden">
      {/* Company Logo in Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <img
          src={logo}
          alt="Company Logo"
          className="h-24 w-28 object-contain"
        />
      </div>

      {/* API Error Banner */}
      {apiError && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white p-2 text-center z-50">
          {apiError}
          <button
            className="ml-2 text-white hover:text-gray-200"
            onClick={() => setApiError(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex-1 flex flex-col h-full max-h-screen">
        {/* Chat Messages Container */}
        <div className="flex-1 overflow-hidden relative">
          <ChatMessages
            messages={messages}
            isLoading={isLoading}
            error={error}
            messagesEndRef={messagesEndRef}
          />
        </div>

        {/* Hover Tooltip */}
        {hoverInfo.show && (
          <HoverTooltip
            content={hoverInfo.content}
            x={hoverInfo.x}
            y={hoverInfo.y}
          />
        )}

        {/* Options Popup - Shown when plus icon is clicked */}
        {showOptionsList && !isFirstSession && (
          <OptionsList
            ref={optionsListRef}
            toggleOptionsList={toggleOptionsList}
            handleOptionHover={handleOptionHover}
            handleOptionLeave={handleOptionLeave}
            handleSelectOption={handleSelectOption}
          />
        )}

        {/* Options Grid - Only shown at start of session, now above input and border */}
        {isFirstSession && showOptions && (
          <OptionsGrid
            handleSelectOption={handleSelectOption}
            handleOptionHover={handleOptionHover}
            handleOptionLeave={handleOptionLeave}
          />
        )}
        {/* Input Area with Plus Button and border */}
        <div className="border-t border-gray-200 bg-white p-4 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {/* Plus Button - Only shown after first session */}
            {!isFirstSession && (
              <button
                ref={plusButtonRef}
                onClick={toggleOptionsList}
                className="p-2 rounded-full bg-[#003366] text-white hover:bg-[#003366] transition-colors relative"
                aria-label="Show options"
              >
                <Plus size={20} />
              </button>
            )}

            {/* Chat Input */}
            <div className="flex-1">
              <ChatInput
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                handleSubmit={handleSubmit}
                handleKeyPress={handleKeyPress}
                isLoading={isLoading}
                deepThinking={deepThinking}
                setDeepThinking={setDeepThinking}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default ChatInterface;
